import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "antd";
import {
  TeamOutlined,
  UsergroupAddOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  RightOutlined,
  DownOutlined,
  WarningFilled,
  RocketOutlined,
} from "@ant-design/icons";
import { SbTable, SbTag, SbButton, SbNotification, SbSpin, SbEmpty, SbTooltip } from "uiCore";
import useAuthStore from "Store/useAuthStore";
import usePAICStore from "Store/usePAICStore";
import PageHeader from "Components/PageHeader";
import {
  PageTransitionStyled,
  ImportStepsBarStyled,
  ImportSectionStyled,
  ImportSectionHeaderStyled,
  ImportEmptyStyled,
  ImportTableInnerStyled,
  ExpandedRolesPanelStyled,
  ExpandedRolesHeaderStyled,
  GroupNameCellStyled,
  RowChevronStyled,
  ExpandToggleStyled,
  PreviewCardStyled,
  PreviewScrollAreaStyled,
  PreviewLoadingRowStyled,
  PreviewActionsStyled,
  PreviewButtonRowStyled,
  PreviewResultStyled,
  PreviewBridgeStripStyled,
  PreviewResultHeaderStyled,
  TreePreviewStyled,
  OrgChartListStyled,
  CompareRowStyled,
  CompareColumnStyled,
  CompareColumnHeaderStyled,
  TreeDividerStyled,
  GateBannerStyled,
  MutedCaptionStyled,
  RequiredMarkStyled,
  RequiredCaptionRowStyled,
  StepTagStyled,
} from "./orgDesign.styles";
import {
  getOrgRedesignPaicProjects,
  getOrgRedesignPaicProjectVersions,
  getOrgRedesignPaicProjectHandshake,
} from "Api/apiService";
import {
  usePaicOrgDesignLookup,
  useGenerateOrgDesign,
  useOrgDesignWorkspace,
  useCanAccessPaicOrgDesignFlow,
  useCanCreateOrgRedesign,
  usePublishedOrgRedesignVersions,
} from "./useOrgDesignData";
import {
  MIN_ROLES_FOR_ORG_DESIGN,
  getFilters,
  PUBLISHED_VERSION_EXISTS_ERROR_CODE,
  AGENT_VERSION_NOT_COMPLETED_MESSAGE,
  AGENT_VERSION_FAILED_MESSAGE,
  PAIC_IMPORT_RETURN_GROUP_KEY,
  activatableRowProps,
} from "./orgDesign.constants";
import { PAIC_SELECTION_STATUS } from "constants/peoplePlusAI.constants";
import { PAIC_ROUTES } from "constants/paicRoutes.constants";
import OrgDesignChartNode from "./components/OrgDesignChartNode";
import OrgDesignSummaryBridge from "./components/OrgDesignSummaryBridge";
import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";

const { Title, Text } = Typography;

const STEPS = [
  { key: 1, label: "1 · Select PAIC Group" },
  { key: 2, label: "2 · Select Version" },
  { key: 3, label: "3 · Preview & Generate" },
];

// gate_status only has one confirmed value today ("invalid" — Org Redesign
// API doc §3 Open Items); anything else is treated as passing rather than
// hardcoding an unconfirmed "valid"/"ready" literal. Shown as a friendly
// Ready/Not Ready label instead of the raw enum string either way.
const readinessMeta = (gateStatus) =>
  gateStatus === "invalid"
    ? { color: "error", icon: <CloseCircleFilled />, label: "Not Ready" }
    : { color: "success", icon: <CheckCircleFilled />, label: "Ready" };

// Builds the "why isn't this ready" tooltip from whichever of the two real
// gates actually failed, rather than a generic "not ready" — only rendered
// when at least one of them is known to have failed.
const readinessTooltip = (record) => {
  const reasons = [];
  if (!record.min_role_met) reasons.push(`at least ${MIN_ROLES_FOR_ORG_DESIGN} roles`);
  if (!record.reporting_complete) reasons.push("completed reporting lines");
  return reasons.length ? `Not ready — needs ${reasons.join(" and ")}.` : null;
};

const yesNoTag = (value, trueColor = "success", falseColor = "error") => (
  <SbTag color={value ? trueColor : falseColor} icon={value ? <CheckCircleFilled /> : <CloseCircleFilled />}>
    {value ? "Yes" : "No"}
  </SbTag>
);

// Header label for the two hard gates the frontend actually checks in
// isGroupReady — the asterisk plus the caption rendered near each table spell
// out what it means, so it isn't just a stray symbol.
const RequiredHeader = ({ label }) => (
  <>
    {label}
    <RequiredMarkStyled>*</RequiredMarkStyled>
  </>
);

// A group can only move on to Step 2 once all three of its own gate
// indicators are green — Min. Role Count, Reporting Lines, and a passing
// Overall Readiness (gate_status). Any one of them failing blocks selection.
//
// Deliberately does NOT grey out on agent-run completion the way Step 2's
// version rows do (see getVersionGates below): GET /paic-projects (doc §2),
// which populates this table, has no selection_status field to grey on —
// that only exists per-version, from GET /paic-project/{id}/versions (doc
// §4). "Agent run not completed" is therefore surfaced one level down, at
// the version a group expands into, not on the group row itself.
const isGroupReady = (group) =>
  Boolean(group.min_role_met) && Boolean(group.reporting_complete) && group.gate_status !== "invalid";

// Single source of truth for a version's two independent gates, so
// isAgentRunCompleted/isVersionReady below can never drift out of sync with
// each other — `ready` is defined IN TERMS OF `agentRunCompleted`, not
// reimplemented next to it.
//  - agentRunCompleted: drives the row's grey-out. A completed run with too
//    few roles still shows as "Not Ready" in the Readiness column, but
//    stays visually normal rather than dimmed — that failure is
//    informational, not "not viewable yet" the way an incomplete/failed
//    agent run is.
//  - ready: drives the Readiness tag/tooltip and the sort order. Requires
//    BOTH gates — the agent run finished AND the minimum role count is met.
const getVersionGates = (version) => {
  const agentRunCompleted = version.selection_status === PAIC_SELECTION_STATUS.COMPLETED;
  return { agentRunCompleted, ready: agentRunCompleted && Boolean(version.min_role_met) };
};
const isAgentRunCompleted = (version) => getVersionGates(version).agentRunCompleted;
const isVersionReady = (version) => getVersionGates(version).ready;

// "Failed" did actually finish running, just unsuccessfully — worth its own
// copy instead of reusing "not completed", which implies waiting will help.
const agentRunStatusMessage = (version) =>
  version.selection_status === PAIC_SELECTION_STATUS.FAILED ? AGENT_VERSION_FAILED_MESSAGE : AGENT_VERSION_NOT_COMPLETED_MESSAGE;

// The one place this label format is built — both handleSelectVersion's
// pre-Step-3 findExisting() check and Step 3's own sourceLabel must agree on
// it exactly, since it's the lookup key into the local saved-versions store.
const buildVersionSourceLabel = (group, version) =>
  group && version ? `${group.project_name} — Version ${version.version}` : "";

// Called right before navigating away to the workspace from a version click
// that auto-redirected (an already-generated version) — see the mount-time
// restore effect below, which reads this back to return Back navigation to
// Select Version instead of resetting to Select PAIC Group.
const rememberReturnToGroup = (projectId) => {
  if (!projectId) return;
  try {
    sessionStorage.setItem(PAIC_IMPORT_RETURN_GROUP_KEY, projectId);
  } catch {
    // sessionStorage can throw in some private-browsing contexts — harmless
    // to skip; worst case Back just lands on Select PAIC Group instead.
  }
};

// A group's roles[] (embedded inline on GET /paic-projects, doc §2) carries
// role_reporting_to as either null (not yet assigned) or an object with the
// manager's own job_title — same shape as /current-org-structure's field.
const reportsToLabel = (role) => {
  if (role.role_reporting_to && typeof role.role_reporting_to === "object") {
    return role.role_reporting_to.job_title;
  }
  if (role.is_apex_role) return "No manager";
  return "Not yet assigned";
};

const NO_REPORTING_LINE_TOOLTIP = "No reporting line set for this role. Contact your HR admin to complete this.";

const roleColumns = [
  { title: "Role", dataIndex: "job_title" },
  { title: "Job Code", dataIndex: "job_code" },
  {
    title: "Reports To",
    key: "reportsTo",
    filterValue: reportsToLabel,
    render: (_, role) => {
      const label = reportsToLabel(role);
      return label === "Not yet assigned" ? (
        <SbTooltip content={NO_REPORTING_LINE_TOOLTIP}>
          <Text type="secondary">{label}</Text>
        </SbTooltip>
      ) : (
        label
      );
    },
  },
  {
    title: "Apex",
    dataIndex: "is_apex_role",
    align: "center",
    width: 90,
    render: (v) => (v == null ? <Text type="secondary">—</Text> : yesNoTag(v)),
  },
];

const ANALYTICS_FROM = "org_design_paic_import";
const EVENTS = ANALYTICS_EVENTS.ORG_DESIGN;

const PaicRecommendedImport = () => {
  const navigate = useNavigate();
  const track = useAnalytics(ANALYTICS_FROM);
  const canAccessPaic = useCanAccessPaicOrgDesignFlow();
  const canCreateOrgRedesign = useCanCreateOrgRedesign();
  const [step, setStep] = useState(1);
  const [ownedGroups, setOwnedGroups] = useState([]);
  const [delegatedGroups, setDelegatedGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const allGroups = useMemo(
    () => [
      ...ownedGroups.map((g) => ({ ...g, _scope: "owned" })),
      ...delegatedGroups.map((g) => ({ ...g, _scope: "delegated" })),
    ],
    [ownedGroups, delegatedGroups]
  );

  // Project ids currently expanded to preview their roles.
  const [expandedGroupKeys, setExpandedGroupKeys] = useState([]);
  const toggleGroupExpanded = (id) =>
    setExpandedGroupKeys((keys) => (keys.includes(id) ? keys.filter((k) => k !== id) : [...keys, id]));

  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);

  // Ready-to-go versions surface first so the user isn't hunting through a
  // page of "Not Ready" rows to find the one they can actually act on — a
  // stable sort keeps each group's own relative (API) order otherwise.
  const sortedVersions = useMemo(
    () => [...versions].sort((a, b) => Number(isVersionReady(b)) - Number(isVersionReady(a))),
    [versions]
  );

  // GET /paic-projects/{id}/handshake (doc §3) — the generation-step gate;
  // returns the final gate_status + eligible roles[] for this PAIC project.
  const [handshake, setHandshake] = useState(null);
  const [loadingHandshake, setLoadingHandshake] = useState(false);

  const [generatedResult, setGeneratedResult] = useState(null);
  // Set when generate() comes back still-pending after its initial wait —
  // shows a manual "Check Status" action instead of auto-polling.
  const [pendingOrgRedesignId, setPendingOrgRedesignId] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const { findExisting } = usePaicOrgDesignLookup();
  const { generate, generating, cancel, checkStatus } = useGenerateOrgDesign();
  const { startFromPaicGenerated } = useOrgDesignWorkspace();
  const { versions: publishedVersions, opening: openingPublished, openVersion } = usePublishedOrgRedesignVersions();
  const refreshToken = useAuthStore((state) => state.refreshToken);
  const setProjectId = usePAICStore((state) => state.setProjectId);
  const setGroupName = usePAICStore((state) => state.setGroupName);
  const setProjectSource = usePAICStore((state) => state.setProjectSource);

  const groupsAbortRef = useRef(null);
  const versionsAbortRef = useRef(null);
  const handshakeAbortRef = useRef(null);

  // Reached by direct URL, this wizard's own route gate (org_redesign) lets
  // the request through even without people_plus_ai:view — so this is a
  // defense-in-depth check, not the primary gate (that's the entry card on
  // OrgDesignLanding not rendering in the first place).
  useEffect(() => {
    if (!canAccessPaic) {
      navigate("/org-design");
      return;
    }
    const controller = new AbortController();
    groupsAbortRef.current = controller;
    (async () => {
      setLoadingGroups(true);
      try {
        const response = await getOrgRedesignPaicProjects(controller.signal);
        setOwnedGroups(response?.owned_paic_groups || []);
        setDelegatedGroups(response?.delegated_paic_groups || []);
      } catch (error) {
        if (error.code === "ERR_CANCELED" || error.name === "AbortError") return; // unmounted before this landed
        SbNotification.error("Unable to load PAIC groups", "Please try again or check your PAIC access.");
      } finally {
        if (!controller.signal.aborted) setLoadingGroups(false);
      }
    })();
    return () => controller.abort();
  }, [canAccessPaic, navigate]);

  // Aborts any request left in flight when the wizard unmounts or a newer
  // selection supersedes it, so a stale response can never land after the
  // UI has already moved on.
  useEffect(
    () => () => {
      versionsAbortRef.current?.abort();
      handshakeAbortRef.current?.abort();
      cancel();
    },
    [cancel]
  );

  // `silent` is for the mount-time restore below — a group that stopped
  // being ready between generating and coming back shouldn't greet the user
  // with a warning toast before they've clicked anything themselves.
  const handleSelectGroup = useCallback(async (project, { silent = false } = {}) => {
    // `silent` is the restore-on-return path, not a user click — tracking it
    // would double-count a group the user selected once.
    if (!silent) {
      track(EVENTS.PAIC_GROUP_SELECTED, {
        projectId: project?.sb_project_unique_id,
        projectName: project?.project_name,
        ready: isGroupReady(project),
      });
    }
    if (!isGroupReady(project)) {
      if (!silent) {
        SbNotification.warning(
          "This group isn't ready yet",
          "Min. Role Count, Reporting Lines, and Overall Readiness all need to pass before you can select a version for this group."
        );
      }
      return;
    }
    versionsAbortRef.current?.abort();
    const controller = new AbortController();
    versionsAbortRef.current = controller;

    setSelectedGroup(project);
    setStep(2);
    setLoadingVersions(true);
    try {
      const response = await getOrgRedesignPaicProjectVersions(project.sb_project_unique_id, controller.signal);
      setVersions(Array.isArray(response) ? response : []);
    } catch (error) {
      if (error.code === "ERR_CANCELED") return;
      // This endpoint has no dedicated "zero versions" response — it errors
      // the same way whether the group truly has no agent-selection versions
      // yet or the request genuinely failed, so it's treated as the former
      // (the far more common case) and rendered through the normal empty
      // state below rather than a scary error toast. Stale rows from
      // whichever group was selected before this one must be cleared here
      // too, or they'd otherwise keep showing under this group's name.
      setVersions([]);
    } finally {
      if (versionsAbortRef.current === controller) setLoadingVersions(false);
    }
  }, [track]);

  // Restores Select Version for whichever group rememberReturnToGroup last
  // recorded — fires once groups have loaded, since it needs allGroups to
  // resolve the stored id back to a real project row. The ref guard makes
  // this a one-shot: it only ever fires right after mount, not on every
  // groups refetch, and the stored id is cleared either way so a later
  // normal visit to this page always starts fresh at Select PAIC Group.
  const restoredGroupRef = useRef(false);
  useEffect(() => {
    if (restoredGroupRef.current || loadingGroups) return;
    restoredGroupRef.current = true;
    let returnToProjectId;
    try {
      returnToProjectId = sessionStorage.getItem(PAIC_IMPORT_RETURN_GROUP_KEY);
      sessionStorage.removeItem(PAIC_IMPORT_RETURN_GROUP_KEY);
    } catch {
      return;
    }
    if (!returnToProjectId) return;
    const matchedGroup = allGroups.find((g) => g.sb_project_unique_id === returnToProjectId);
    if (matchedGroup) handleSelectGroup(matchedGroup, { silent: true });
  }, [loadingGroups, allGroups, handleSelectGroup]);

  const handleSelectVersion = useCallback(async (version) => {
    track(EVENTS.PAIC_VERSION_SELECTED, {
      selectionId: version?.sb_selection_unique_id,
      roleCount: version?.job_ids?.length ?? 0,
      selectionStatus: version?.selection_status,
      minRoleMet: !!version?.min_role_met,
    });
    if (version.selection_status !== PAIC_SELECTION_STATUS.COMPLETED) {
      SbNotification.warning("This version isn't ready yet", agentRunStatusMessage(version));
      return;
    }
    if (!version.min_role_met) {
      SbNotification.warning(
        "This version isn't ready yet",
        `This version has ${version.job_ids?.length ?? 0} role${version.job_ids?.length === 1 ? "" : "s"} — it must meet the minimum role count of ${MIN_ROLES_FOR_ORG_DESIGN} before you can continue to the next step.`
      );
      return;
    }

    // Was this exact version already generated in THIS browser session
    // (explicitly saved from the workspace)? findExisting is local/session-
    // scoped, so it catches a same-session reopen even before the
    // project-wide published list (fetched once on mount, below) has any
    // chance to reflect it.
    const versionSourceLabel = buildVersionSourceLabel(selectedGroup, version);
    const existingForVersion = findExisting(versionSourceLabel);
    if (existingForVersion) {
      startFromPaicGenerated(
        versionSourceLabel,
        { roles: existingForVersion.snapshot.roles, ...existingForVersion.snapshot },
        version.sb_selection_unique_id
      );
      rememberReturnToGroup(selectedGroup?.sb_project_unique_id);
      navigate("/org-design/workspace");
      return;
    }

    // Otherwise fall back to server truth: a PAIC-sourced Org Redesign is
    // published immediately on Generate (no separate "publish" step, see
    // saveVersion's own comment in useOrgDesignData.js). GET /versions (doc
    // §8) returns every published row's own sb_project_unique_id and
    // version — for a PAIC source, that version IS the PAIC agent-selection
    // version number it was generated from (confirmed against a live
    // project with independently published Version 1 and Version 2 rows) —
    // so matching on all four fields finds THIS version's own design, if
    // one exists, without ever matching a sibling version's row (see BUG
    // #1882).
    const existingPublished = publishedVersions.find(
      (v) =>
        v.source_type === "PAIC" &&
        v.sb_project_unique_id === selectedGroup?.sb_project_unique_id &&
        v.version === version.version &&
        v.published_status === "PUBLISHED"
    );
    if (existingPublished) {
      const opened = await openVersion(existingPublished);
      if (opened) {
        rememberReturnToGroup(selectedGroup?.sb_project_unique_id);
        navigate("/org-design/workspace");
        return;
      }
    }

    handshakeAbortRef.current?.abort();
    const controller = new AbortController();
    handshakeAbortRef.current = controller;

    setSelectedVersion(version);
    setStep(3);
    setGeneratedResult(null);
    setPendingOrgRedesignId(null);
    setLoadingHandshake(true);
    try {
      const response = await getOrgRedesignPaicProjectHandshake(selectedGroup.sb_project_unique_id, controller.signal);
      setHandshake(response || null);
    } catch (error) {
      if (error.code === "ERR_CANCELED") return;
      SbNotification.error("Unable to load roles", "Could not load role data for this version.");
    } finally {
      if (handshakeAbortRef.current === controller) setLoadingHandshake(false);
    }
  }, [selectedGroup, findExisting, startFromPaicGenerated, publishedVersions, openVersion, navigate, track]);

  // User-initiated only (never automatic — see Step 2's empty state below,
  // which is the only caller): sends the user to this exact PAIC group in
  // the PAIC module so they can create/run the version that's missing here.
  // Guards on sb_project_unique_id rather than just selectedGroup so a
  // malformed/partial group row can't write an empty projectId into the
  // shared PAIC store.
  const handleGoToPaicGroup = () => {
    if (!selectedGroup?.sb_project_unique_id) return;
    setProjectId(selectedGroup.sb_project_unique_id);
    setGroupName(selectedGroup.project_name);
    setProjectSource(selectedGroup._scope);
    // Same restore mechanism the auto-redirect branches above use — a
    // browser Back from the PAIC module returns here to Select Version for
    // this same group (which re-fetches, so a version created there now
    // shows up) instead of resetting all the way to Select PAIC Group.
    rememberReturnToGroup(selectedGroup.sb_project_unique_id);
    navigate(PAIC_ROUTES.GROUP_AGENTS);
  };

  // Steps back one wizard screen at a time (3 -> 2 -> 1); only Step 1
  // actually leaves the wizard. Cancels an in-flight generation so its
  // eventual result can't land on a since-changed selection.
  const BACK_LABELS = { 1: "Back to Organisation Design", 2: "Back to Select PAIC Group", 3: "Back to Select Version" };
  const backLabel = BACK_LABELS[step];
  const handleBack = () => {
    if (generating) cancel();
    if (step === 1) {
      navigate("/org-design");
    } else {
      setGeneratedResult(null);
      setPendingOrgRedesignId(null);
      setStep((s) => s - 1);
    }
  };

  const handleStepClick = (targetStep) => {
    if (targetStep >= step) return;
    if (generating) cancel();
    setGeneratedResult(null);
    setPendingOrgRedesignId(null);
    setStep(targetStep);
  };

  const sourceLabel = buildVersionSourceLabel(selectedGroup, selectedVersion);
  const roleCount = handshake?.roles?.length ?? 0;
  // Only "invalid" is a confirmed gate_status value today (doc §3 Open
  // Items) — treat anything else returned as passing rather than hardcoding
  // an unconfirmed "valid"/"ready" literal.
  const gatePassed = Boolean(handshake) && handshake.gate_status !== "invalid";
  // NOTE: no "already generated — open it" branch here on purpose.
  // handleSelectVersion runs this exact findExisting(sourceLabel) lookup
  // before Step 3 is ever reached and redirects straight to the workspace
  // on a match — so by the time this renders, there is no such match left
  // to show. Re-adding a check here would be dead code for a case that
  // can't happen through the UI.

  const handleOpenInWorkspace = (result) => {
    track(EVENTS.OPEN_IN_WORKSPACE, {
      selectionId: selectedVersion?.sb_selection_unique_id,
      roleCount: result?.roles?.length ?? 0,
      newRoleCount: result?.new_roles?.length ?? 0,
    });
    startFromPaicGenerated(sourceLabel, result, selectedVersion?.sb_selection_unique_id);
    navigate("/org-design/workspace");
  };

  // Selection id captured up front and re-checked on resolve — if the user
  // backed out to a different version while this ran, drop the stale result.
  const handleGenerate = async () => {
    // Same resource POST /create-org-redesign-version is gated on
    // server-side (org_redesign:create) — checked up front so a user
    // missing it gets this same messaging instead of a raw 403.
    if (!canCreateOrgRedesign) {
      SbNotification.error("Unable to generate Organisation Design", "You don't have permission to create a new Organisation Design version.");
      return;
    }
    const requestedSelectionId = selectedVersion?.sb_selection_unique_id;
    // Regenerate reuses this handler — an existing result on screen is what
    // tells the two apart.
    track(generatedResult ? EVENTS.REGENERATE : EVENTS.GENERATE_INITIATED, {
      selectionId: requestedSelectionId,
      roleCount,
    });
    setPendingOrgRedesignId(null);
    try {
      const result = await generate(requestedSelectionId, refreshToken);
      if (selectedVersion?.sb_selection_unique_id !== requestedSelectionId) return;
      if (result?.pending) {
        // The job outlived the initial wait. Worth its own event: the gap
        // between this and GENERATE_COMPLETED is how often users are left
        // polling by hand.
        track(EVENTS.GENERATE_PENDING, { selectionId: requestedSelectionId, roleCount });
        setPendingOrgRedesignId(result.orgRedesignId);
        return;
      }
      track(EVENTS.GENERATE_COMPLETED, {
        selectionId: requestedSelectionId,
        roleCount: result?.roles?.length ?? 0,
        newRoleCount: result?.new_roles?.length ?? 0,
      });
      setGeneratedResult(result);
    } catch (error) {
      if (error.code === "ERR_CANCELED" || error.name === "AbortError") return; // user hit Cancel — no error to show
      // Not a real failure — there's already something to open for this
      // group, so point the user at it instead of showing a scary error.
      if (error.response?.data?.error_code === PUBLISHED_VERSION_EXISTS_ERROR_CODE) {
        SbNotification.info(
          "A published Organisation Design already exists",
          `${selectedGroup?.project_name || "This PAIC group"} already has a published version. You can open it from the Previous Iterations list below.`
        );
        navigate("/org-design");
        return;
      }
      SbNotification.error("Unable to generate Organisation Design", "Please try again or check your PAIC access.");
    }
  };

  // On-demand recheck for a still-generating job — one GET, no auto-retry.
  const handleCheckStatus = async () => {
    if (!pendingOrgRedesignId) return;
    setCheckingStatus(true);
    try {
      const result = await checkStatus(pendingOrgRedesignId);
      track(EVENTS.GENERATE_STATUS_CHECKED, {
        orgRedesignId: pendingOrgRedesignId,
        ready: !!result,
      });
      if (!result) {
        SbNotification.info("Still generating", "Your Organisation Design isn't ready yet — check back again shortly.");
        return;
      }
      setPendingOrgRedesignId(null);
      setGeneratedResult(result);
    } catch (error) {
      SbNotification.error("Unable to check status", "Please try again.");
    } finally {
      setCheckingStatus(false);
    }
  };

  // Expanding a group row previews its roles inline — the row itself still
  // proceeds to Step 2 on click (antd's own expand caret is a separate
  // control, so the two never conflict).
  const renderGroupRoles = (group) => (
    <ExpandedRolesPanelStyled onClick={(e) => e.stopPropagation()}>
      <ExpandedRolesHeaderStyled>
        <span className="roles-title">Roles in this group ({group.roles?.length ?? 0})</span>
        <span className="roles-hint">
          {isGroupReady(group)
            ? "Click anywhere on the row to continue"
            : "Not ready yet — Min. Role Count, Reporting Lines, and Overall Readiness must all pass first"}
        </span>
      </ExpandedRolesHeaderStyled>
      {group.roles?.length ? (
        <SbTable
          columns={roleColumns}
          dataSource={group.roles}
          rowKey="sb_unique_id"
          pagination={false}
          size="small"
        />
      ) : (
        <MutedCaptionStyled>No role details available for this group.</MutedCaptionStyled>
      )}
    </ExpandedRolesPanelStyled>
  );

  // Fields match GET /paic-projects verbatim (doc §2) — role_count/
  // min_role_met/reporting_complete/gate_status are computed live by the
  // backend, not stored, so no project_number/evaluation-status columns
  // exist on this response (those belonged to the old PAIC evaluation API).
  const groupColumns = [
    {
      title: "Group Name",
      dataIndex: "project_name",
      ellipsis: true,
      width: 320,
      render: (name) => (
        <GroupNameCellStyled title={name}>
          <TeamOutlined />
          <span className="name-text">{name}</span>
        </GroupNameCellStyled>
      ),
    },
    {
      title: "Ownership",
      dataIndex: "_scope",
      align: "center",
      width: 140,
      filters: [
        { text: "My Groups", value: "owned" },
        { text: "Delegated to Me", value: "delegated" },
      ],
      onFilter: (value, record) => record._scope === value,
      render: (scope) =>
        scope === "owned" ? (
          <SbTag color="processing" icon={<TeamOutlined />}>My Groups</SbTag>
        ) : (
          <SbTag color="default" icon={<UsergroupAddOutlined />}>Delegated</SbTag>
        ),
    },
    {
      title: "Role Count",
      dataIndex: "role_count",
      align: "center",
      width: 120,
      filters: getFilters(allGroups, "role_count"),
      onFilter: (value, record) => record.role_count === value,
    },
    {
      title: <RequiredHeader label="Min. Role Count" />,
      dataIndex: "min_role_met",
      align: "center",
      width: 180,
      filters: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      onFilter: (value, record) => Boolean(record.min_role_met) === value,
      render: (v, record) =>
        v ? (
          yesNoTag(v)
        ) : (
          <SbTooltip content={`Needs at least ${MIN_ROLES_FOR_ORG_DESIGN} roles to run Organisation Design — this group currently has ${record.role_count}.`}>
            {yesNoTag(v)}
          </SbTooltip>
        ),
    },
    {
      title: <RequiredHeader label="Reporting Lines" />,
      dataIndex: "reporting_complete",
      align: "center",
      width: 180,
      filters: [
        { text: "Complete", value: true },
        { text: "Incomplete", value: false },
      ],
      onFilter: (value, record) => Boolean(record.reporting_complete) === value,
      render: (v) => {
        const tag = <SbTag color={v ? "success" : "warning"}>{v ? "Complete" : "Incomplete"}</SbTag>;
        return v ? (
          tag
        ) : (
          <SbTooltip content="All roles need a reporting line assigned before this group can be used. Contact your HR admin to complete this.">
            {tag}
          </SbTooltip>
        );
      },
    },
    {
      title: "Overall Readiness",
      dataIndex: "gate_status",
      align: "center",
      width: 170,
      // Only "invalid" is a confirmed gate_status value today (doc §3 Open
      // Items) — filter on the same Ready/Not Ready split the column itself
      // renders, rather than the raw unconfirmed enum.
      filters: [
        { text: "Ready", value: "ready" },
        { text: "Not Ready", value: "invalid" },
      ],
      onFilter: (value, record) =>
        value === "invalid" ? record.gate_status === "invalid" : record.gate_status !== "invalid",
      render: (v, record) => {
        const meta = readinessMeta(v);
        const tag = <SbTag color={meta.color} icon={meta.icon}>{meta.label}</SbTag>;
        const tooltip = v === "invalid" ? readinessTooltip(record) : null;
        return tooltip ? <SbTooltip content={tooltip}>{tag}</SbTooltip> : tag;
      },
    },
    {
      title: "",
      key: "expandToggle",
      width: 36,
      render: (_, record) =>
        record.roles?.length ? (
          <ExpandToggleStyled
            $expanded={expandedGroupKeys.includes(record.sb_project_unique_id)}
            {...activatableRowProps(() => toggleGroupExpanded(record.sb_project_unique_id))}
            onClick={(e) => {
              e.stopPropagation();
              toggleGroupExpanded(record.sb_project_unique_id);
            }}
            onKeyDown={(e) => {
              // Nested inside a table row that's itself keyboard-activatable
              // (selects the group) — stop propagation so Enter/Space here
              // only toggles the preview, not both actions at once.
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                toggleGroupExpanded(record.sb_project_unique_id);
              }
            }}
          >
            <DownOutlined />
          </ExpandToggleStyled>
        ) : null,
    },
  ];

  // Fields match GET /paic-project/{id}/versions verbatim (doc §4).
  const versionColumns = [
    {
      title: "Version",
      dataIndex: "version",
      // Auto filter, labelled like the cell.
      filterLabel: (v) => `Version ${v}`,
      render: (v) => <Text strong>{`Version ${v}`}</Text>,
    },
    {
      title: "Agent Count",
      key: "agentCount",
      align: "center",
      width: 140,
      filters: getFilters(versions, "agent_unique_ids", (r) => r.agent_unique_ids?.length ?? 0),
      onFilter: (value, record) => (record.agent_unique_ids?.length ?? 0) === value,
      render: (_, r) => r.agent_unique_ids?.length ?? 0,
    },
    {
      title: <RequiredHeader label="Role Count" />,
      key: "roleCount",
      align: "center",
      width: 120,
      filters: getFilters(versions, "job_ids", (r) => r.job_ids?.length ?? 0),
      onFilter: (value, record) => (record.job_ids?.length ?? 0) === value,
      render: (_, r) => r.job_ids?.length ?? 0,
    },
    {
      title: "Min. Role Count",
      dataIndex: "min_role_met",
      align: "center",
      width: 140,
      filters: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      onFilter: (value, record) => Boolean(record.min_role_met) === value,
      render: (v, record) =>
        v ? (
          yesNoTag(v)
        ) : (
          <SbTooltip
            content={`This version has ${record.job_ids?.length ?? 0} role${record.job_ids?.length === 1 ? "" : "s"} — it must meet the minimum role count of ${MIN_ROLES_FOR_ORG_DESIGN} to continue to the next step.`}
          >
            {yesNoTag(v)}
          </SbTooltip>
        ),
    },
    {
      // Ready only once every gate this table checks has passed — agent run
      // completion and Min. Role Count. Either one failing shows Not Ready
      // with the specific reason(s) in the tooltip. The row's own grey-out
      // is a narrower condition (agent run completion only) — see
      // isAgentRunCompleted above — so a row can show Not Ready here while
      // staying visually un-greyed.
      title: "Readiness",
      key: "readiness",
      align: "center",
      width: 140,
      filters: [
        { text: "Ready", value: true },
        { text: "Not Ready", value: false },
      ],
      onFilter: (value, record) => isVersionReady(record) === value,
      render: (_, record) => {
        const ready = isVersionReady(record);
        const tag = (
          <SbTag color={ready ? "success" : "error"} icon={ready ? <CheckCircleFilled /> : <CloseCircleFilled />}>
            {ready ? "Ready" : "Not Ready"}
          </SbTag>
        );
        if (ready) return tag;
        const reasons = [];
        if (record.selection_status !== PAIC_SELECTION_STATUS.COMPLETED) reasons.push(`${agentRunStatusMessage(record)}.`);
        if (!record.min_role_met) {
          reasons.push(
            `Needs at least ${MIN_ROLES_FOR_ORG_DESIGN} roles to proceed — this version has ${record.job_ids?.length ?? 0}.`
          );
        }
        return <SbTooltip content={reasons.join(" ")}>{tag}</SbTooltip>;
      },
    },
    {
      title: "",
      key: "chevron",
      width: 36,
      render: () => <RowChevronStyled><RightOutlined /></RowChevronStyled>,
    },
  ];

  if (!canAccessPaic) return null;

  return (
    <PageTransitionStyled>
      <PageHeader
        title="Start with a PAIC Recommended Org"
        subtitle="Import a completed PAIC run and generate an AI-recommended org design from its results."
        onBack={handleBack}
        backLabel={backLabel}
      />

      <ImportStepsBarStyled>
        {STEPS.map((s) => (
          <StepTagStyled
            key={s.key}
            color={step === s.key ? "processing" : "default"}
            $clickable={s.key < step}
            onClick={() => handleStepClick(s.key)}
            {...activatableRowProps(() => handleStepClick(s.key))}
          >
            {s.label}
          </StepTagStyled>
        ))}
      </ImportStepsBarStyled>

      {step === 1 && (
        <ImportSectionStyled>
          <ImportSectionHeaderStyled>
            <TeamOutlined />
            Select a PAIC Group
            <span className="section-count">{loadingGroups ? "" : `· ${allGroups.length}`}</span>
          </ImportSectionHeaderStyled>
          {!loadingGroups && allGroups.length === 0 ? (
            <ImportEmptyStyled>
              <SbEmpty description="No PAIC groups owned by or delegated to you yet." />
            </ImportEmptyStyled>
          ) : (
            <>
              {!loadingGroups && (
                <RequiredCaptionRowStyled>
                  <MutedCaptionStyled>
                    <RequiredMarkStyled>*</RequiredMarkStyled> Required to select this group and continue.
                  </MutedCaptionStyled>
                </RequiredCaptionRowStyled>
              )}
              <ImportTableInnerStyled>
                <SbTable
                  columns={groupColumns}
                  dataSource={allGroups}
                  rowKey="sb_project_unique_id"
                  loading={loadingGroups}
                  sticky
                  tableLayout="fixed"
                  pagination={{ defaultPageSize: 10, pageSizeOptions: ["10", "20", "30"], showSizeChanger: true }}
                  rowClassName={(record) => (isGroupReady(record) ? "" : "row-not-ready")}
                  expandable={{
                    expandedRowRender: renderGroupRoles,
                    expandedRowKeys: expandedGroupKeys,
                    onExpand: (_, record) => toggleGroupExpanded(record.sb_project_unique_id),
                    rowExpandable: (record) => (record.roles?.length ?? 0) > 0,
                    showExpandColumn: false,
                  }}
                  onRow={(record) => ({ onClick: () => handleSelectGroup(record), ...activatableRowProps(() => handleSelectGroup(record)) })}
                />
              </ImportTableInnerStyled>
            </>
          )}
        </ImportSectionStyled>
      )}

      {step === 2 && (
        <ImportSectionStyled>
          <ImportSectionHeaderStyled>
            <TeamOutlined />
            {selectedGroup?.project_name}
            <span className="section-count">{loadingVersions ? "" : `· ${versions.length} version(s)`}</span>
          </ImportSectionHeaderStyled>
          {!loadingVersions && versions.length === 0 ? (
            <ImportEmptyStyled>
              <SbEmpty description="No agent-selection versions found for this group yet — create one in the PAIC module first.">
                <SbButton buttonType="secondary" onClick={handleGoToPaicGroup}>
                  Go to PAIC Group
                </SbButton>
              </SbEmpty>
            </ImportEmptyStyled>
          ) : (
            <>
              {!loadingVersions && (
                <RequiredCaptionRowStyled>
                  <MutedCaptionStyled>
                    <RequiredMarkStyled>*</RequiredMarkStyled> Required to select this version and continue.
                  </MutedCaptionStyled>
                </RequiredCaptionRowStyled>
              )}
              <ImportTableInnerStyled>
                <SbTable
                  columns={versionColumns}
                  dataSource={sortedVersions}
                  rowKey="sb_selection_unique_id"
                  loading={loadingVersions || openingPublished}
                  sticky
                  pagination={{ defaultPageSize: 10, pageSizeOptions: ["10", "20", "30"], showSizeChanger: true }}
                  rowClassName={(record) => (isAgentRunCompleted(record) ? "" : "row-not-ready")}
                  onRow={(record) => ({ onClick: () => handleSelectVersion(record), ...activatableRowProps(() => handleSelectVersion(record)) })}
                />
              </ImportTableInnerStyled>
            </>
          )}
        </ImportSectionStyled>
      )}

      {step === 3 && (
        <PreviewScrollAreaStyled>
          <PreviewCardStyled>
            <Title level={5}>{sourceLabel}</Title>

            {loadingHandshake ? (
              <PreviewLoadingRowStyled role="status" aria-label="Checking role eligibility">
                <SbSpin size="small" />
              </PreviewLoadingRowStyled>
            ) : (
              <>
                <Text type="secondary">
                  <strong>{roleCount}</strong> eligible role{roleCount === 1 ? "" : "s"} in this PAIC project.
                </Text>

                {!gatePassed && (
                  <GateBannerStyled>
                    <WarningFilled />
                    <span>
                      Organisation Design needs at least {MIN_ROLES_FOR_ORG_DESIGN} roles to run against a group (apex/min-5 gate).
                      This selection has {roleCount}.
                    </span>
                  </GateBannerStyled>
                )}

                {gatePassed && (
                  <PreviewActionsStyled>
                    <SbButton buttonType="primary" icon={<RocketOutlined />} onClick={handleGenerate} loading={generating} disabled={!!pendingOrgRedesignId}>
                      {generating ? "Generating Organisation Design…" : "Generate Organisation Design"}
                    </SbButton>
                  </PreviewActionsStyled>
                )}

                {/* This single request can take longer than a typical one — the
                    backend holds it open until the generation job actually
                    finishes — so keep a clear, dedicated loading row up until the
                    response comes back, not just the button's own spinner. */}
                {generating && (
                  <PreviewLoadingRowStyled>
                    <SbSpin size="small" />
                    <Text type="secondary">
                      Generating your Organisation Design — this runs as a background job and can take several minutes. Feel free to cancel and check back later.
                    </Text>
                    <SbButton buttonType="action" size="small" onClick={cancel}>Cancel</SbButton>
                  </PreviewLoadingRowStyled>
                )}

                {/* Generation was still running after the initial wait — rather
                    than polling in the background indefinitely with no way to
                    tell "still running" apart from "job died," this waits for
                    the user to ask again, the same way the PAIC dashboard's own
                    group dropdown re-checks status on open instead of polling. */}
                {pendingOrgRedesignId && !generating && (
                  <PreviewLoadingRowStyled>
                    <Text type="secondary">
                      Still generating — this can take a while for larger groups. Check back whenever you like.
                    </Text>
                    <SbButton buttonType="secondary" size="small" onClick={handleCheckStatus} loading={checkingStatus}>
                      Check Status
                    </SbButton>
                  </PreviewLoadingRowStyled>
                )}

                {generatedResult && !generating && (
                  <PreviewResultStyled>
                    <PreviewResultHeaderStyled>
                      <CheckCircleFilled />
                      Organisation Design generated — {generatedResult.roles.length} existing role{generatedResult.roles.length === 1 ? "" : "s"},{" "}
                      {generatedResult.new_roles.length} proposed role{generatedResult.new_roles.length === 1 ? "" : "s"}.
                    </PreviewResultHeaderStyled>

                    {generatedResult.stats && (
                      <PreviewBridgeStripStyled>
                        <OrgDesignSummaryBridge stats={generatedResult.stats} statusCounts={generatedResult.status_counts} />
                      </PreviewBridgeStripStyled>
                    )}

                    {/* Current vs Proposed side by side — seeing what actually
                        changed (which roles were Augmented/Reduced/marked
                        Redundant, and which are brand-new/Created) is far more
                        useful than only showing the proposed result on its
                        own. Each node's status tag/color already comes from
                        the tree engine, so Created/Augmented/etc. render
                        correctly here the same way they do everywhere else. */}
                    <CompareRowStyled>
                      <CompareColumnStyled>
                        <CompareColumnHeaderStyled>Current</CompareColumnHeaderStyled>
                        <TreePreviewStyled>
                          <OrgChartListStyled>
                            {generatedResult.before_tree.ch.map((node) => (
                              <OrgDesignChartNode key={node.id} node={node} editable={false} onEdit={() => {}} />
                            ))}
                          </OrgChartListStyled>
                        </TreePreviewStyled>
                      </CompareColumnStyled>
                      <TreeDividerStyled />
                      <CompareColumnStyled>
                        <CompareColumnHeaderStyled $variant="proposed">Proposed</CompareColumnHeaderStyled>
                        <TreePreviewStyled>
                          <OrgChartListStyled>
                            {generatedResult.after_tree.ch.map((node) => (
                              <OrgDesignChartNode key={node.id} node={node} editable={false} onEdit={() => {}} />
                            ))}
                          </OrgChartListStyled>
                        </TreePreviewStyled>
                      </CompareColumnStyled>
                    </CompareRowStyled>

                    <PreviewButtonRowStyled>
                      <SbButton buttonType="primary" icon={<RightOutlined />} onClick={() => handleOpenInWorkspace(generatedResult)}>
                        Open in Workspace
                      </SbButton>
                      <SbButton buttonType="secondary" icon={<RocketOutlined />} onClick={handleGenerate} loading={generating}>
                        Regenerate
                      </SbButton>
                    </PreviewButtonRowStyled>
                  </PreviewResultStyled>
                )}
              </>
            )}
          </PreviewCardStyled>
        </PreviewScrollAreaStyled>
      )}
    </PageTransitionStyled>
  );
};

export default PaicRecommendedImport;
